import { useContext } from 'react';

import PhoneNumber from 'awesome-phonenumber';
import { startCase } from 'lodash';
import moment from 'moment';

import { MainContext, t } from '@ezbobdev/ui-framework';

const stringFormatType = {
  CAPITAL: 'capitalize',
  LOWER: 'lowerCase',
  UPPER: 'upperCase',
};

const addressFieldsFormattersOptionsDefault = {
  name: {
    formatter: stringFormatType.CAPITAL,
    isNextLine: false,
  },
  line1: {
    formatter: stringFormatType.CAPITAL,
    isNextLine: false,
  },
  line2: {
    formatter: stringFormatType.CAPITAL,
    isNextLine: false,
  },
  line3: {
    formatter: stringFormatType.CAPITAL,
    isNextLine: false,
  },
  region: {
    formatter: stringFormatType.CAPITAL,
    isNextLine: false,
  },
  town: {
    formatter: stringFormatType.CAPITAL,
    isNextLine: false,
  },
  state: {
    formatter: stringFormatType.CAPITAL,
    isNextLine: false,
  },
  country: {
    formatter: stringFormatType.UPPER,
    isNextLine: false,
  },
  pobox: {
    formatter: stringFormatType.CAPITAL,
    isNextLine: false,
  },
  postcode: {
    formatter: stringFormatType.UPPER,
    isNextLine: false,
  },
};

const isFloatingPointNumber = (number) => {
  return number % 1 !== 0;
};

export const useFormatter = () => {
  const { actions } = useContext(MainContext);

  const {
    currency,
    countryCode,
    minimumFractionDigits,
    maximumFractionDigits,
    roundDownFractionDigits = false,
    primaryDateFormat = 'DD/MM/YYYY',
    primaryDateTimeFormat = 'DD/MM/YYYY hh:mm',
    secondaryDateFormat = 'Do MMMM, YYYY',
    currencySign = '',
    useCustomCurrencyFormat = false,
    addressSeparator = ', ',
    addressFieldsFormattersOptions = addressFieldsFormattersOptionsDefault,
  } = actions.settings.getUserConfigProp('locals');

  /**
   * @description format a number based on Intl https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl
   * @param {Number} number - The number which to be formatted
   * @param {Object} options - Object with format currency formatting options
   * @param {Number} options.minFraction - The minimum number of fraction digits to use
   * @param {Number} options.maxFraction - The maximum number of fraction digits to use
   * @param {Boolean} options.roundDownFraction - Round down/floor to .00
   * @param {Boolean} options.showTrailingZeros - shows trailing zeros if the number we pass is a whole one (e.g. 100 -> 100.00)
   * @returns {string} formattedNumber
   */

  const formatCurrency = (
    number,
    options = {
      formattingOptionsName: '',
      minFraction: minimumFractionDigits,
      maxFraction: maximumFractionDigits,
      roundDownFraction: roundDownFractionDigits,
      showTrailingZeros: false,
    },
  ) => {
    const { minFraction, maxFraction, roundDownFraction, showTrailingZeros } = options;
    const isNumber = number !== undefined && number !== null && !isNaN(Number(number));
    if (isNumber) {
      let isFloatNumber = isFloatingPointNumber(number);

      if (isFloatNumber && roundDownFraction) {
        number = Math.floor(number);
        isFloatNumber = isFloatingPointNumber(number);
      }
      if (useCustomCurrencyFormat && currencySign) {
        const num = number.toLocaleString('en', {
          minimumFractionDigits: minFraction,
          maximumFractionDigits: maxFraction,
        });
        return `${currencySign}${num}`;
      }
      const formatterOptions = {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: minFraction,
        maximumFractionDigits: maxFraction,
      };
      if (!showTrailingZeros && !isFloatNumber) {
        formatterOptions.minimumFractionDigits = 0;
        formatterOptions.maximumFractionDigits = 0;
      }
      const formatter = new Intl.NumberFormat(countryCode, formatterOptions);
      const formattedNumber = formatter.format(number);
      return formattedNumber;
    }
    return number;
  };

  /**
   * @description Format percent `value` based on Intl https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl
   * @param {Number} value - The value which to be formatted
   * @param {Object} options - Object with percentage formatting options
   * @param {Number} options.minFraction - The minimum number of fraction digits to use
   * @param {Number} options.maxFraction - The maximum number of fraction digits to use
   * @param {String} options.prefix - Prefix added to the value (coming from the translations)
   * @returns {string} formattedValue
   */
  const formatPercentage = (value, options = {}) => {
    if (typeof value !== 'number') return '';
    const {
      minFraction = minimumFractionDigits,
      maxFraction = maximumFractionDigits,
      formatDefaultValue = false,
      prefix = '',
    } = options;
    const formatterOptions = {
      style: 'percent',
      minimumFractionDigits: minFraction,
      maximumFractionDigits: maxFraction,
    };

    const formatter = new Intl.NumberFormat(countryCode, formatterOptions);

    const formattedValue = formatter.format(formatDefaultValue ? value : value / 100);

    if (prefix) {
      return `${prefix} ${formattedValue}`;
    }

    return formattedValue;
  };

  /**
   * @description Percentage formatting with custom rounding of the APR value based on businessType & formatting options configured
   * @param {Number} value APR value to be formatted
   * @param {Object} options - Object with APR formatting options
   * @param {Number} options.minFraction - The minimum number of fraction digits to use
   * @param {Number} options.maxFraction - The maximum number of fraction digits to use
   * @param {Boolean} options.useCustomPercentageFormat - enables custom rounding: if 3rd or onward decimal > 0 -> it will increase 2nd decimal by 1
   * @returns formatted value
   */
  const formatAPR = (
    value,
    options = {
      minFraction: minimumFractionDigits,
      maxFraction: maximumFractionDigits,
      useCustomPercentageFormat: false,
    },
  ) => {
    const { minFraction, maxFraction, useCustomPercentageFormat } = options;
    const formatterOptions = {
      style: 'percent',
      minimumFractionDigits: minFraction,
      maximumFractionDigits: maxFraction,
    };
    const formatter = new Intl.NumberFormat(countryCode, formatterOptions);
    //matches only numbers with 3rd decimal 1-4 or 4th and onward 1-9;
    // if 3rd decimal is 5-9, standard rounding to 2 decimals takes place
    const regex = /\d*\.\d{2}((0*[1-4]{1}0*[1-9]*)|(0+[1-9]+))\d*/;
    if (useCustomPercentageFormat && regex.test(value)) {
      return formatter.format((value + 0.01) / 100);
    }

    return formatter.format(value / 100);
  };

  /**
   * @description Format the address to a standard for the UI way
   * @param {Object} address An object which contains all the address components (line1, city etc..)
   * @param {Array} filteredAddressParts An array which contains the address components to use (empty if we don't want to filter them)
   * @returns {String} formattedAddress
   */
  const formatAddress = (address, filteredAddressParts = []) => {
    const { name, line1, line2, line3, region, town, state, country, pobox, postcode } = addressFieldsFormattersOptions;

    const filteredAddressPartsFromConfig = actions.settings.getUserConfigProp('locals.filteredAddressParts');

    const effectiveFilteredAddressParts =
      filteredAddressParts.length > 0 ? filteredAddressParts : filteredAddressPartsFromConfig || [];

    let addressPartsOrdered = [
      {
        name: 'name',
        value: address.name,
        formatter: name.formatter,
        isNextLine: name.isNextLine,
      },
      {
        name: 'line1',
        value: address.line1,
        formatter: line1.formatter,
        isNextLine: line1.isNextLine,
      },
      {
        name: 'line2',
        value: address.line2,
        formatter: line2.formatter,
        isNextLine: line2.isNextLine,
      },
      {
        name: 'line3',
        value: address.line3,
        formatter: line3.formatter,
        isNextLine: line3.isNextLine,
      },
      {
        name: 'region',
        value: address.region,
        formatter: region.formatter,
        isNextLine: region.isNextLine,
      },
      {
        name: 'town',
        value: address.town,
        formatter: town.formatter,
        isNextLine: town.isNextLine,
      },
      { name: 'state', value: address.state, formatter: state.formatter, isNextLine: state.isNextLine },
      { name: 'country', value: address.country, formatter: country.formatter, isNextLine: country.isNextLine },
      { name: 'pobox', value: address.pobox, formatter: pobox.formatter, isNextLine: pobox.isNextLine },
      { name: 'postcode', value: address.postcode, formatter: postcode.formatter, isNextLine: postcode.isNextLine },
    ];

    if (effectiveFilteredAddressParts.length > 0) {
      addressPartsOrdered = addressPartsOrdered.filter((part) =>
        effectiveFilteredAddressParts.some((el) => el === part.name),
      );
    }

    const formattedAddressParts = addressPartsOrdered.reduce((result, currentItem) => {
      if (currentItem.value) {
        let formattedValue = '';

        switch (currentItem.formatter) {
          case stringFormatType.LOWER: {
            formattedValue = currentItem.value.toLowerCase();
            break;
          }

          case stringFormatType.UPPER: {
            formattedValue = currentItem.value.toUpperCase();
            break;
          }

          case stringFormatType.CAPITAL: {
            formattedValue = startCase(currentItem.value.toLowerCase());
            break;
          }

          default:
            formattedValue = currentItem.value;
        }

        if (currentItem.isNextLine) {
          formattedValue = `<br role="presentation"/>${formattedValue}`;
        }

        result.push(formattedValue);
      }

      return result;
    }, []);

    const formattedAddress = formattedAddressParts.join(addressSeparator);

    return formattedAddress;
  };

  /**
   * @description Format a date to presentational String
   * @param {Date|String} date The date which to be formatted
   * @param {Object} options - Object with date formatting options
   * @param {String} options.outFormat Format which to be applied, the default is locals.primaryDateFormat
   * @returns {String} formattedDate
   */
  const formatDate = (
    date,
    options = {
      outFormat: primaryDateFormat,
    },
  ) => {
    if (!date) return date;

    const { outFormat } = options;
    const momentDate = moment(date);
    const formattedDate = momentDate.format(outFormat);

    return formattedDate;
  };

  /**
   * @description Format a date to presentational String
   * @param {Date|String} date The date which to be formatted (with timestamp)
   * @param {Object} options - Object with date formatting options
   * @param {String} options.outFormat Format which to be applied, the default is locals.primaryDateTimeFormat
   * @returns {String} formattedDateTime
   */
  const formatDateTime = (
    date,
    options = {
      outFormat: primaryDateTimeFormat,
    },
  ) => {
    if (!date) return date;

    const { outFormat } = options;
    const momentDate = moment(date);
    const formattedDateTime = momentDate.format(outFormat);

    return formattedDateTime;
  };

  /**
   * @description Subtract 1 day from the date, used for offer/case expiration, and format a date to presentational String
   * @param {Date|String} date The date which to be formatted
   * @param {Object} options - Object with expirationDate formatting options
   * @param {String} options.outFormat Format which to be applied, the default is locals.secondaryDateFormat
   * @returns {String} formattedDate
   */
  const formatExpirationDate = (
    date,
    options = {
      outFormat: secondaryDateFormat,
    },
  ) => {
    const { outFormat } = options;
    const momentDate = moment(date);
    momentDate.subtract(1, 'days');

    const formattedDate = momentDate.format(outFormat);

    return formattedDate;
  };

  /**
   * @description Concatenates a number `monthsValue` with a translated text suffix e.g "24 Months"
   * @param {Number} monthsValue  - The number representation i.e 12, 15, 24 etc..
   * @param {Object} options - Object with months formatting options
   * @param {Boolean} option.shorten - If true then it will return `monthsValue` + the first letter of the months translation i.e if the translation is "Months" then it will return "M"
   * @returns {string} formattedValue
   */
  const formatMonths = (
    monthsValue,
    options = {
      shorten: false,
    },
  ) => {
    const { shorten } = options;
    const monthsText = t('globals.month', { count: monthsValue });

    const formattedValue = `${monthsValue} ${shorten ? monthsText[0] : monthsText}`;

    return formattedValue;
  };

  /**
   * @description Concatenates a number `daysValue` with a translated text suffix e.g "20 Days"
   * @param {Number} daysValue  - The number representation i.e 12, 15, 24 etc..
   * @param {Object} options - Object with days formatting options
   * @param {Boolean} option.shorten - If true then it will return `daysValue` + the first letter of the days translation i.e if the translation is "Days" then it will return "D"
   * @returns {string} formattedValue
   */
  const formatDays = (
    daysValue,
    options = {
      shorten: false,
    },
  ) => {
    if (typeof daysValue !== 'number') return '';

    const { shorten } = options;
    const daysText = t('globals.days');
    const formattedValue = `${daysValue} ${shorten ? daysText[0] : daysText}`;

    return formattedValue;
  };

  /**
   * @description This function give us possibility to get the month amount from amount of days.
   * @param {Number} days  - The number of days that will be used for calculation.
   * @returns {string} formattedValue
   */
  const formatDaysToMonths = (days) => {
    let monthsValue = null;

    if (days % 30 === 0) {
      monthsValue = Math.round(moment.duration(days, 'days').asMonths());
    } else {
      monthsValue = moment.duration(days, 'days').asMonths().toFixed(2);
    }

    const periodText = t('globals.month', { count: monthsValue });

    const formatedDaysText = `${monthsValue} ${periodText}`;

    return formatedDaysText;
  };

  /**
   * @description Concatenates first, middle, last from `fullName` object
   * @param {Object} fullName - An object which properties are first, middle, last
   * @returns {string} formattedValue
   */
  const formatFullName = (fullName) => {
    let formattedValue = '';
    const isFullnameObj = typeof fullName === 'object' && fullName !== null;

    if (isFullnameObj) {
      const { firstName = '', middleName = '', lastName = '' } = fullName;

      if (middleName) {
        formattedValue = `${firstName} ${middleName} ${lastName}`;
      } else {
        formattedValue = `${firstName} ${lastName}`;
      }
    }
    return formattedValue;
  };

  /**
   * @description Takes enum as `loanPurposeValue` and using it returns the translated value
   * @param {String} loanPurposeValue
   * @returns {string} formattedValue
   */
  const formatLoanPurpose = (loanPurposeValue) => {
    const typeOfBusiness = actions.case.getCaseValue('loanee.typeOfBusiness');
    const formattedValue = t(`loanRequest.loanPurpose.${typeOfBusiness}.values.${loanPurposeValue}`);

    return formattedValue;
  };

  /**
   * @description Takes a number and returns a formatted string with `precision` after the decimal point
   * @param {Number} numValue - The number which to be formatted
   * @param {Object} options - Object with precision options
   * @param {Number} options.precision - The length of the digits after the decimal point
   * @returns {string} result
   */
  const toFixed = (
    numValue,
    options = {
      precision: 2,
    },
  ) => {
    const { precision } = options;
    const isFloatingPoint = isFloatingPointNumber(numValue);
    if (!isFloatingPoint) return numValue.toFixed(precision);

    const regexp = new RegExp(`^-?\\d+(?:\\.\\d{0,${precision}})?`, 'gi');
    let resultParts = regexp.exec(numValue.toString());

    if (!resultParts) return numValue;

    let result = resultParts[0];
    const digitsAfterDecimalPoint = result.split('.')[1];
    if (digitsAfterDecimalPoint.length < precision) {
      result = result.padEnd(result.length + precision - digitsAfterDecimalPoint.length, '0');
    }

    return result;
  };

  /**
   * @description Mask a given string from `start` to `end`
   * @param {string} value - The value/text, which to be masked
   * @param {Object} options - Object with masking options
   * @param {Number} options.start - Start index of the masked text
   * @param {Number} options.end - End index of the masked part
   * @param {char} options.maskingSymbol - Symbols which to mask the `value` characters
   * @param {Array} options.omitChars - Array of chars, that should not be masked
   * @returns {string} maskedText
   */
  const maskText = (value, options = {}) => {
    const { start = 0, end = 5, maskingSymbol = 'X', omitChars = [] } = options || {};

    const stringifiedValue = value.toString();
    let valueArray = stringifiedValue.split('');
    let counter = 0;
    let maskedText = '';

    for (let i = 0; i <= valueArray.length; i += 1) {
      let isOmitted = omitChars.includes(valueArray[i]);
      if (!isOmitted) {
        if (counter >= start && counter < end) {
          valueArray[i] = maskingSymbol;
        }
        counter++;
        if (counter >= end) {
          break;
        }
      }
    }

    maskedText = valueArray.join('');

    return maskedText;
  };

  /**
   * @description Takes an integer value which represents elapsed miliseconds and returns it as formatted value
   * @param {Integer} value
   * @returns {String} formattedValue
   */
  const formatDuration = (value) => {
    const duration = moment.duration(value);
    return moment.utc(duration.asMilliseconds()).format('mm:ss');
  };

  /**
   * formatStringToRange
   *
   * @description Converts a string that represent a range to an object with range values
   * @param {String} value A text representing range e.g. 1-50
   * @param {RegExp} pattern Regex which to be used to match the digits in the range string
   * @returns {{from: Number, to: Number}} Object that contains the extracted digits from the range string
   */
  const formatStringToRange = (value, pattern = /^\s?(\w+)\s?-\s?(\w+)\s?$/) => {
    const [, from, to] = value.match(pattern) ?? [];
    const result = { from: Number(from), to: Number(to) };

    return result;
  };

  /**
   * formatRangeToString
   *
   * @description Convert an object, that represent a range, to a string that represent a range
   * @param {{from: Number, to: Number}} range An object that reprenset a range
   * @param {String} delimiter The delimitar between `from` and `to` range values e.g. 0`-`50 where the delimiter is `-`
   * @returns {String} string that reprensets a range
   */
  const formatRangeToString = (range, delimiter = '-') => {
    const { from, to } = range ?? {};

    if (typeof from === 'undefined' || typeof to === 'undefined') return '';

    return `${from}${delimiter}${to}`;
  };

  /**
   * @description Format a phone number to international format
   * @param {String} phoneNumber
   * @returns formatted phone number
   */
  const formatPhoneNumber = (phoneNumber) => {
    const pn = new PhoneNumber(phoneNumber, countryCode);
    return pn.getNumber('international') || phoneNumber;
  };

  return {
    toFixed,
    formatAddress,
    formatDate,
    formatDateTime,
    formatExpirationDate,
    formatCurrency,
    formatPercentage,
    formatAPR,
    formatMonths,
    formatDays,
    formatFullName,
    formatLoanPurpose,
    formatDaysToMonths,
    formatDuration,
    maskText,
    formatRangeToString,
    formatStringToRange,
    formatPhoneNumber,
  };
};
